import { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert, Modal } from '@components/ui';
import {
  CARD,
  CTA,
  GHOST,
  PAGE_BTN,
  ICON_BTN,
  ROW_HOVER,
  TONE_SURFACE,
  TONE_BAR,
} from '@components/ui/designTokens';
import {
  PlusIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useCustomers, useCreateCustomer, useUpdateCustomer } from '../hooks/useCustomers';
import { useQueryClient } from '@tanstack/react-query';
import { CustomerForm } from './CustomerForm';
import { CustomerImportModal } from './CustomerImportModal';
import type { Customer, CreateCustomerInput } from '../types/customer.types';
import { displayCustomerName as displayName, displayCustomerTaxId as displayTaxId } from '../utils/customerDisplay';
import { Sparkline } from '@components/charts/MiniCharts';
// SUBSCRIPTIONS_DISABLED: import { useConsumption } from '@/features/subscriptions/hooks/useConsumption';
import Swal from 'sweetalert2';
import { useSupportConfig } from '@features/support';

/**
 * Clientes — Claude Design · Clientes.dc.html
 *
 * El diseño cambia la tabla de 7 columnas por un "ledger" de fichas de fila,
 * pero no se perdio ningun dato: RUC (+ DV), nombre, tipo, email, telefono,
 * fecha de creacion y las dos acciones siguen visibles en cada fila. Agrupa
 * RUC+DV bajo el nombre y email+telefono en una sola celda de contacto.
 *
 * La franja de resumen sale unicamente de los clientes que devolvio la pagina
 * actual (taxIdType, email, phone, createdAt): el endpoint responde
 * { customers, total, page, limit, totalPages } y no trae conteos por tipo ni
 * cobertura de contacto de alcance global. El unico dato global es `total`, y
 * es el unico que se muestra sin el rotulo "en esta pagina".
 *
 * Dos cosas del mock quedaron fuera a proposito:
 *   - los chips de filtro por tipo, porque filtrarian solo la pagina cargada
 *     mientras la paginacion sigue hablando del total del servidor;
 *   - el atajo ⌘K del buscador, porque no hay tal atajo implementado.
 * Ninguna de las dos mostraba informacion que la pantalla ya tuviera.
 */

/**
 * Familia visual del cliente — antes derivaba de `taxIdType` (iTipoRec de
 * mdl17); migrado a mdl03/core, el dato real y estable es `customerKind`
 * (Particular vs Comercial), que el legado modela con dos formularios
 * distintos (`_FormParticularCustomer.cshtml` / `_FormBusinessCustomer.cshtml`).
 */
type CustomerKind = 'PARTICULAR' | 'BUSINESS';

const kindOf = (customer: Customer): CustomerKind => customer.customerKind;

const KIND_STYLES: Record<
  CustomerKind,
  { legend: string; strip: string; swatch: string; chip: string; dot: string; avatar: string }
> = {
  PARTICULAR: {
    legend: 'Particular',
    strip: '#12c4b2',
    swatch: 'bg-teal-500',
    chip: 'bg-teal-500/10 text-[#0d9488]',
    dot: 'bg-teal-500',
    avatar: 'bg-teal-500/10 text-[#0d9488]',
  },
  BUSINESS: {
    legend: 'Comercial',
    strip: '#6b239e',
    swatch: 'bg-[#6b239e]',
    chip: 'bg-[#6b239e]/10 text-[#6b239e]',
    dot: 'bg-[#6b239e]',
    avatar: 'bg-[#6b239e]/10 text-[#6b239e]',
  },
};

/** Orden de la leyenda y de la barra de composicion. */
const KIND_ORDER: CustomerKind[] = ['PARTICULAR', 'BUSINESS'];

const MONTHS_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/** "10 · Ene · 26", como en el diseño. Sin locale de date-fns, que el proyecto no usa. */
const formatShortDate = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return `${String(d.getDate()).padStart(2, '0')} · ${MONTHS_SHORT[d.getMonth()]} · ${String(d.getFullYear()).slice(-2)}`;
};

/** "20 · Ene · 2026" para la tarjeta de alta mas reciente. */
const formatLongDate = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return `${String(d.getDate()).padStart(2, '0')} · ${MONTHS_SHORT[d.getMonth()]} · ${d.getFullYear()}`;
};

/** Conectores que no aportan inicial ("Comercial del Norte" → CN, no CD). */
const INITIAL_STOPWORDS = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'y', 'e', 'en', 'of', 'the', 'and', 'a']);

const initialsOf = (name?: string): string => {
  if (!name) return '?';
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w) && !INITIAL_STOPWORDS.has(w.toLowerCase()));
  const initials = words.slice(0, 2).map((w) => w[0]).join('');
  return initials.toUpperCase() || '?';
};

/** Grilla del ledger: franja de color, avatar, y las 5 celdas de datos + acciones. */
/**
 * Grilla del ledger. Los minimos son los del contenido: las celdas de texto ya
 * se contienen con `min-w-0` + `truncate` + `title`, asi que apretar los tracks
 * trunca antes pero no solapa. El track de Tipo queda fijo en 136px porque el
 * chip es `whitespace-nowrap` y la etiqueta mas larga ("Consumidor Final") mide
 * ~131px.
 */
const ROW_GRID =
  'grid min-w-[732px] grid-cols-[4px_40px_minmax(140px,1.6fr)_136px_minmax(150px,1.4fr)_92px_76px] items-center gap-x-3';
const COL_HEAD =
  'text-[11px] font-bold uppercase leading-[1.25] tracking-[0.09em] text-secondary-400';
const METRIC_LABEL =
  'font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400';

/** Circunferencia del arco de cobertura (r = 24). */
const ARC_LENGTH = 2 * Math.PI * 24;

export function CustomerListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [_deletingCustomer, _setDeletingCustomer] = useState<Customer | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search input: wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if there are at least 3 characters or if it's empty (to show all)
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setDebouncedSearch(searchInput);
        setPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Detectar si es búsqueda por RUC (solo números y guiones) o por nombre
  const isRucSearch = /^[\d-]+$/.test(debouncedSearch);

  const { data, isLoading, error } = useCustomers({
    page,
    limit: 10,
    name: isRucSearch ? undefined : debouncedSearch,
    taxId: isRucSearch ? debouncedSearch : undefined,
  });
  const createMutation = useCreateCustomer();
  /*
   * Con el asistente activo, el fallo del alta ya se muestra en el modal del
   * asistente (lo dispara el onError de la mutacion) CON la causa real y la
   * explicacion. Este Swal generico quedaba encima diciendo "intenta de nuevo",
   * que no aporta nada y obliga a un clic extra justo cuando el cliente esta
   * trabado. Si el asistente esta apagado, se conserva: sin el, el cliente se
   * quedaria sin ningun aviso de que fallo.
   */
  const { assistantEnabled } = useSupportConfig();
  const updateMutation = useUpdateCustomer();
  // SUBSCRIPTIONS_DISABLED: const { validateCanAddCustomer } = useConsumption();

  // El backend ya pagina, usamos directamente los datos que devuelve
  const limit = data?.limit || 10;
  const totalPages = data?.totalPages || 1;
  const customers = data?.customers || [];

  /**
   * Resumen de la pagina actual. Se calcula sobre los clientes que ya estan en
   * memoria (los mismos que se listan abajo): tipo, email, telefono y fecha de
   * creacion. No hay ninguna consulta extra ni ningun valor estimado.
   */
  const summary = useMemo(() => {
    const byKind: Record<CustomerKind, number> = {
      PARTICULAR: 0,
      BUSINESS: 0,
    };
    let withContact = 0;
    let latest: Customer | null = null;
    const byMonth = new Map<string, number>();

    for (const customer of customers) {
      byKind[kindOf(customer)] += 1;
      if (customer.email?.trim() && customer.phone?.trim()) withContact += 1;

      const created = customer.createdAt ? new Date(customer.createdAt) : null;
      if (created && !isNaN(created.getTime())) {
        const key = `${created.getFullYear()}-${String(created.getMonth()).padStart(2, '0')}`;
        byMonth.set(key, (byMonth.get(key) ?? 0) + 1);
        if (!latest || (latest.createdAt && new Date(latest.createdAt).getTime() < created.getTime())) {
          latest = customer;
        }
      }
    }

    // Altas por mes de creacion, en orden cronologico. Si la pagina cae en un
    // solo mes la serie tiene un punto y el sparkline no dibuja: no hay
    // tendencia que mostrar.
    const months = [...byMonth.keys()].sort();

    return {
      byKind,
      withContact,
      latest: latest as Customer | null,
      altasByMonth: months.map((m) => byMonth.get(m)!),
    };
  }, [customers]);

  const handleImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    Swal.fire({
      icon: 'success',
      title: 'Importacion exitosa',
      text: 'Los clientes fueron importados correctamente',
      confirmButtonColor: '#3085d6',
    });
  };

  const handleOpenCreate = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  const handleSubmit = async (data: CreateCustomerInput) => {
    /* SUBSCRIPTIONS_DISABLED
    if (!editingCustomer) {
      const validation = validateCanAddCustomer();
      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de clientes alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });
        if (result.isConfirmed) {
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
    }
    */

    try {
      if (editingCustomer) {
        await updateMutation.mutateAsync({ id: editingCustomer.id, data });
        handleCloseModal();

        // Success message for update
        Swal.fire({
          title: '¡Actualizado!',
          text: 'El cliente ha sido actualizado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      } else {
        await createMutation.mutateAsync(data);
        handleCloseModal();

        // Reset to page 1 and clear search to see the new customer
        setPage(1);
        setSearchInput('');
        setDebouncedSearch('');

        // Success message for create
        Swal.fire({
          title: '¡Creado exitosamente!',
          text: 'El cliente ha sido creado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      }
    } catch (error: any) {
      console.error('❌ Error al guardar cliente:', error);

      // Combina ambas ramas: el mensaje real del backend (Aludra ya explica qué
      // falló) pero solo se muestra si el asistente NO está activo; cuando está,
      // el asistente maneja el error (ver nota en la declaración de assistantEnabled).
      const backendMessage = error?.response?.data?.message;
      if (!assistantEnabled) {
        Swal.fire({
          title: 'Error',
          text: backendMessage || 'Ocurrió un error al guardar el cliente. Por favor, intenta de nuevo.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#d33',
        });
      }
    }
  };

  // No hay `handleDelete`: Aludra (mdl03/core) no soporta eliminar clientes,
  // igual que el portal legado (CustomerController.cs) — no se agrega un
  // botón para una acción que el backend no puede cumplir.

  const contactPct = customers.length > 0 ? summary.withContact / customers.length : 0;

  return (
    <div className="font-display">
      {/* ============ CABECERA ============ */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
            arriba (quedaba de mas junto al breadcrumb).
          */}
          <nav className="flex items-baseline gap-2">
            <span className="text-[14px] text-secondary-400">Inicio</span>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Clientes
            </h1>
          </nav>
          <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
            Gestiona tu cartera de clientes
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" className={GHOST} onClick={() => setIsImportModalOpen(true)} data-testid="customer-list-import-button">
            <ArrowUpTrayIcon className="h-3.5 w-3.5" />
            Importar Excel
          </button>
          <button type="button" className={CTA} onClick={handleOpenCreate} data-testid="customer-list-new-button">
            <PlusIcon className="h-3.5 w-3.5" />
            Nuevo Cliente
          </button>
        </div>
      </div>

      {/*
        ============ FRANJA DE RESUMEN ============
        `total` es el unico dato global que devuelve el endpoint. Composicion
        por tipo, cobertura de contacto y alta mas reciente salen de los
        clientes de la pagina actual y van rotulados como tales.
      */}
      {!isLoading && !error && customers.length > 0 && (
        <div className="mt-6">
          {/*
            Un solo rotulo de alcance para toda la franja, como en Facturas
            emitidas: todo lo de abajo es de la pagina actual. El unico dato
            global (`total`) va como nota de la primera tarjeta.
          */}
          <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-secondary-400">
            Resumen de esta página
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)]">
          {/* Clientes en la pagina + composicion por tipo */}
          <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.violet}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.violet}`} />
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className={METRIC_LABEL}>Clientes</div>
                <div className="mt-1.5 flex items-baseline gap-2.5">
                  <span className="text-[40px] font-extrabold leading-none tracking-[-0.035em] text-ink-850 dark:text-white">
                    {customers.length}
                  </span>
                  <span className="whitespace-nowrap font-mono text-[12px] text-secondary-400">
                    de {data?.total ?? customers.length} en total
                  </span>
                </div>
              </div>
              <div className="w-[150px] shrink-0">
                <div className="mb-1.5 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary-400">
                  Altas por mes
                </div>
                <Sparkline values={summary.altasByMonth} color="#0d9488" gradientId="spark-clientes" />
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary-400">
                Por tipo
              </div>
              <div className="flex h-2.5 items-center gap-[3px] overflow-hidden rounded-full">
                {KIND_ORDER.filter((k) => summary.byKind[k] > 0).map((k) => (
                  <div
                    key={k}
                    className="h-full"
                    style={{ flex: summary.byKind[k], background: KIND_STYLES[k].strip }}
                    title={`${KIND_STYLES[k].legend}: ${summary.byKind[k]}`}
                  />
                ))}
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-[22px] gap-y-2 text-[12px] text-secondary-500 dark:text-secondary-400">
                {KIND_ORDER.filter((k) => summary.byKind[k] > 0).map((k) => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-[2px] ${KIND_STYLES[k].swatch}`} />
                    {KIND_STYLES[k].legend}{' '}
                    <b className="font-bold text-ink-850 dark:text-white">{summary.byKind[k]}</b>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Cobertura de contacto */}
          <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.teal}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.teal}`} />
            <div className={METRIC_LABEL}>Cobertura contacto</div>
            <div className="mt-3.5 flex items-center gap-4">
              <svg viewBox="0 0 60 60" className="h-[58px] w-[58px] shrink-0" aria-hidden="true">
                <circle cx="30" cy="30" r="24" fill="none" stroke="#eef2f6" strokeWidth="6" />
                <circle
                  cx="30"
                  cy="30"
                  r="24"
                  fill="none"
                  stroke="#0d9488"
                  strokeWidth="6"
                  strokeDasharray={ARC_LENGTH}
                  strokeDashoffset={ARC_LENGTH * (1 - contactPct)}
                  strokeLinecap="round"
                  transform="rotate(-90 30 30)"
                />
              </svg>
              <div>
                <div className="flex items-baseline gap-[3px]">
                  <span className="text-[28px] font-extrabold leading-none tracking-[-0.03em] text-ink-850 dark:text-white">
                    {summary.withContact}
                  </span>
                  <span className="text-sm font-semibold text-secondary-400">/{customers.length}</span>
                </div>
                <div className="mt-1 text-xs leading-[1.35] text-secondary-500 dark:text-secondary-400">
                  con correo
                  <br />
                  <span className="text-secondary-400">y teléfono</span>
                </div>
              </div>
            </div>
          </div>

          {/* Alta mas reciente de la pagina */}
          <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.neutral}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.neutral}`} />
            <div className={METRIC_LABEL}>Alta más reciente</div>
            {summary.latest ? (
              <div className="mt-3 flex items-center gap-3">
                <span
                  className={`inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] text-[14px] font-extrabold tracking-[-0.02em] ${
                    KIND_STYLES[kindOf(summary.latest)].avatar
                  }`}
                >
                  {initialsOf(displayName(summary.latest))}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-bold text-ink-850 dark:text-white" title={displayName(summary.latest)}>
                    {displayName(summary.latest)}
                  </div>
                  <div className="mt-0.5 font-mono text-[12px] text-secondary-400">
                    {formatLongDate(summary.latest.createdAt)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-3 text-[14px] text-secondary-400">Sin fecha de creación disponible</div>
            )}
          </div>
          </div>
        </div>
      )}

      {/* ============ BÚSQUEDA ============ */}
      <div className="mt-6">
        <div className="relative rounded-xl border border-[#ececec] bg-white transition-colors focus-within:border-[#0d9488] focus-within:shadow-[0_0_0_3px_rgba(18,196,178,.10)] dark:border-secondary-600 dark:bg-secondary-800">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o RUC (mínimo 3 caracteres)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="customer-list-search-input"
            className="w-full rounded-xl border-0 bg-transparent py-[13px] pl-11 pr-11 text-sm text-ink-850 outline-none placeholder:text-secondary-400 focus:ring-0 dark:text-white"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              data-testid="customer-list-clear-search-button"
              aria-label="Limpiar búsqueda"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchInput.length > 0 && searchInput.length < 3 && (
          <p className="mt-2 text-[13px] text-secondary-500 dark:text-secondary-400">
            Escribe al menos 3 caracteres para buscar
          </p>
        )}
        {searchInput !== debouncedSearch && searchInput.length >= 3 && (
          <p className="mt-2 flex items-center gap-1.5 text-[13px] text-secondary-500 dark:text-secondary-400">
            <Spinner size="sm" />
            Buscando...
          </p>
        )}
      </div>

      {/* ============ LEDGER ============ */}
      <div className={`${CARD} mt-5 overflow-hidden`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="p-5">
            <Alert variant="danger" title="Error al cargar clientes" data-testid="customer-list-error-alert">
              Ocurrió un error al cargar los clientes. Por favor, intenta de nuevo.
            </Alert>
          </div>
        ) : customers.length === 0 ? (
          <div className="py-12 text-center">
            <UsersIcon className="mx-auto h-12 w-12 text-secondary-300 dark:text-secondary-600" />
            <h3 className="mt-3 text-sm font-bold text-ink-850 dark:text-white">No hay clientes</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {debouncedSearch
                ? 'No se encontraron resultados para tu búsqueda'
                : 'Comienza creando tu primer cliente'}
            </p>
            {!debouncedSearch && (
              <div className="mt-6">
                <button type="button" className={CTA} onClick={handleOpenCreate} data-testid="customer-list-empty-new-button">
                  <PlusIcon className="h-4 w-4" />
                  Nuevo Cliente
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/*
              Scroll horizontal en vez de esconder columnas en pantallas
              angostas: el mock oculta Tipo / Contacto / Alta por debajo de
              1080px, y eso seria perder informacion de la fila.
            */}
            <div className="overflow-x-auto">
              {/* Cabecera de columnas */}
              <div
                data-testid="customer-list-table"
                className={`${ROW_GRID} border-b border-[#f0f0f0] bg-secondary-50 py-3.5 pl-0 pr-[22px] dark:border-secondary-700 dark:bg-secondary-900`}
              >
                <div />
                <div />
                <div className={COL_HEAD}>Cliente · RUC</div>
                <div className={COL_HEAD}>Tipo</div>
                <div className={COL_HEAD}>Contacto</div>
                <div className={COL_HEAD}>Alta</div>
                <div className={`${COL_HEAD} pr-1 text-right`}>Acciones</div>
              </div>

              {customers.map((customer) => {
                const kind = kindOf(customer);
                const style = KIND_STYLES[kind];
                const typeLabel = style.legend;
                const name = displayName(customer);
                const taxId = displayTaxId(customer);

                return (
                  <div
                    key={customer.id}
                    data-testid={`customer-list-row-${customer.id}`}
                    className={`${ROW_GRID} border-t border-dashed border-[#eef0f2] py-3.5 pl-0 pr-[22px] ${ROW_HOVER} first:border-t-0 dark:border-secondary-700`}
                  >
                    {/* Franja de color por tipo */}
                    <div className="mr-1 h-full w-1 self-stretch" style={{ background: style.strip }} />

                    {/* Avatar: iniciales para Particular, icono para Comercial */}
                    <span
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-[10px] text-[14px] font-extrabold tracking-[-0.02em] ${style.avatar}`}
                      title={typeLabel}
                    >
                      {kind === 'BUSINESS' ? (
                        <BuildingOfficeIcon className="h-[18px] w-[18px]" />
                      ) : (
                        <UserIcon className="h-[18px] w-[18px]" />
                      )}
                    </span>

                    {/* Nombre + RUC/Cédula + DV */}
                    <div className="min-w-0">
                      <div
                        className="truncate text-[15px] font-bold tracking-[-0.01em] text-ink-850 dark:text-white"
                        title={name}
                      >
                        {name}
                      </div>
                      <div className="mt-[3px] inline-flex items-center gap-2">
                        <span className="font-mono text-[12px] text-secondary-500 dark:text-secondary-400">
                          {taxId}
                        </span>
                        {customer.dv && (
                          <span className="rounded bg-[#f4f4f4] px-1.5 py-px font-mono text-[11px] font-semibold text-secondary-400 dark:bg-secondary-700 dark:text-secondary-300">
                            DV {customer.dv}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Tipo: `max-w-full` + truncate como red de seguridad. Las
                        4 etiquetas mapeadas entran holgadas, pero `typeLabel`
                        cae al `taxIdType` crudo si Aludra manda uno sin mapear —
                        y sin esto ese valor se saldria de la celda. */}
                    <div className="min-w-0">
                      <span
                        className={`inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold ${style.chip}`}
                        title={typeLabel}
                      >
                        <span className={`h-[5px] w-[5px] shrink-0 rounded-full ${style.dot}`} />
                        <span className="truncate">{typeLabel}</span>
                      </span>
                    </div>

                    {/* Contacto: email + teléfono */}
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-1.5 text-[13px] text-ink-850 dark:text-white">
                        <EnvelopeIcon className="h-3 w-3 shrink-0 text-secondary-400" />
                        <span className="truncate" title={customer.email || undefined}>
                          {customer.email || '-'}
                        </span>
                      </div>
                      <div className="mt-[3px] flex min-w-0 items-center gap-1.5 font-mono text-[12px] text-secondary-500 dark:text-secondary-400">
                        <PhoneIcon className="h-3 w-3 shrink-0 text-secondary-400" />
                        <span className="truncate">{customer.phone || '-'}</span>
                      </div>
                    </div>

                    {/* Fecha de creación */}
                    <div className="whitespace-nowrap font-mono text-xs text-secondary-500 dark:text-secondary-400">
                      {formatShortDate(customer.createdAt)}
                    </div>

                    {/* Acciones — sin eliminar: Aludra (mdl03/core) no lo soporta */}
                    <div className="inline-flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(customer)}
                        data-testid={`customer-list-edit-button-${customer.id}`}
                        aria-label={`Editar ${name}`}
                        title="Editar"
                        className={ICON_BTN}
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Paginación */}
            {data && (
              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#f0f0f0] bg-secondary-50 px-[22px] py-4 dark:border-secondary-700 dark:bg-secondary-900">
                <div className="text-[13px] text-secondary-500 dark:text-secondary-400">
                  Mostrando{' '}
                  <span className="font-mono font-bold text-ink-850 dark:text-white">
                    {(page - 1) * limit + 1}
                  </span>{' '}
                  a{' '}
                  <span className="font-mono font-bold text-ink-850 dark:text-white">
                    {Math.min(page * limit, data.total)}
                  </span>{' '}
                  de <span className="font-mono font-bold text-ink-850 dark:text-white">{data.total}</span>{' '}
                  clientes
                  <span className="mx-2.5 text-secondary-300">·</span>
                  Página <span className="font-mono font-bold text-ink-850 dark:text-white">{page}</span> de{' '}
                  <span className="font-mono font-bold text-ink-850 dark:text-white">{totalPages}</span>
                </div>
                <div className="inline-flex gap-2">
                  <button
                    type="button"
                    className={`${PAGE_BTN} gap-1.5 px-3.5`}
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    data-testid="customer-list-prev-page-button"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={`${PAGE_BTN} gap-1.5 px-3.5`}
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    data-testid="customer-list-next-page-button"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/*
        Modal Create/Edit

        `xl` (max-w-4xl) en vez de `4xl` (max-w-6xl), y sin <ModalBody>: ese
        wrapper fuerza h-[70vh], asi que el modal media lo mismo con dos campos
        que con veinte. Ahora CustomerForm maneja su propio alto y crece con el
        contenido.
      */}
      <Modal
        data-testid="customer-list-form-modal"
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        kicker="Catálogo · Cliente"
        title={editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
        description={
          editingCustomer
            ? 'Actualiza los datos fiscales y de contacto del cliente.'
            : 'Registra los datos fiscales y de contacto del nuevo cliente.'
        }
        icon={<UsersIcon />}
        size="xl"
      >
        <CustomerForm
          key={editingCustomer?.id || 'new'}
          customer={editingCustomer || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Modal Import Excel */}
      <CustomerImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
